import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalUser, ProjectInfo, Incidence } from '../../types';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

interface ReportBugWidgetProps {
    currentUser?: GlobalUser;
    currentProject?: ProjectInfo & { id: string };
    onAddIncidence: (incidence: Incidence) => void;
}

const ReportBugWidget: React.FC<ReportBugWidgetProps> = ({ currentUser, currentProject, onAddIncidence }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [moduleName, setModuleName] = useState('Otros / General');
    const [actionDescription, setActionDescription] = useState('');
    const [expectedResult, setExpectedResult] = useState('');
    const [actualResult, setActualResult] = useState('');
    const [priority, setPriority] = useState<'Baja' | 'Media' | 'Alta'>('Media');
    const [screenshot, setScreenshot] = useState<string | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    
    const location = useLocation();

    // Auto-detect module name based on current route path
    useEffect(() => {
        const path = location.pathname;
        if (path === '/') setModuleName('Dashboard / Inicio');
        else if (path === '/edt') setModuleName('EDT (Estructura de Trabajo)');
        else if (path === '/planning') setModuleName('Planificación');
        else if (path === '/quality') setModuleName('Control de Calidad');
        else if (path === '/payments') setModuleName('Subcontratos y Pagos');
        else if (path === '/reports') setModuleName('Informes');
        else setModuleName('Otros / General');
    }, [location.pathname, isOpen]);

    // Listen to paste events for clipboard screenshots
    useEffect(() => {
        if (!isOpen) return;
        const handlePaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    if (!blob) continue;
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        if (event.target?.result) {
                            setScreenshot(event.target.result as string);
                        }
                    };
                    reader.readAsDataURL(blob);
                }
            }
        };
        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [isOpen]);

    // Listen to custom window event to trigger opening modal from headers
    useEffect(() => {
        const handleOpen = () => setIsOpen(true);
        window.addEventListener('open-bug-report', handleOpen);
        return () => window.removeEventListener('open-bug-report', handleOpen);
    }, []);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("La imagen excede el límite de 5MB. Por favor, selecciona una más pequeña.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setScreenshot(event.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobilePhotoCapture = async () => {
        try {
            const image = await Camera.getPhoto({
                quality: 80,
                allowEditing: false,
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Prompt, // Prompts user to choose Camera or Gallery
                promptLabelHeader: 'Adjuntar Imagen de Incidencia',
                promptLabelPhoto: 'Seleccionar de Galería',
                promptLabelPicture: 'Tomar Fotografía'
            });
            
            if (image.dataUrl) {
                // Estimate size from base64 DataUrl length
                const sizeInBytes = (image.dataUrl.length * 3) / 4;
                if (sizeInBytes > 5 * 1024 * 1024) {
                    alert("La imagen excede el límite de 5MB. Por favor, selecciona una más pequeña.");
                    return;
                }
                setScreenshot(image.dataUrl);
            }
        } catch (error) {
            console.log("User cancelled or camera error:", error);
            // Gracefully handles cancellation to prevent locking the modal or forcing an upload!
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!actionDescription.trim() || !expectedResult.trim() || !actualResult.trim()) return;

        setIsSubmitting(true);

        const newIncidence: Incidence = {
            id: `INC-${Date.now()}`,
            userEmail: currentUser?.email || 'anonimo@ldconstructora.cl',
            userName: currentUser?.name || 'Usuario Invitado',
            projectId: currentProject?.id || 'GLOBAL',
            projectName: currentProject?.name || 'General / Ninguna',
            moduleName,
            actionDescription: actionDescription.trim(),
            expectedResult: expectedResult.trim(),
            actualResult: actualResult.trim(),
            priority,
            status: 'Pendiente',
            creationDate: new Date().toISOString(),
            screenshot
        };

        // Simulated local delay for premium feedback experience
        setTimeout(() => {
            onAddIncidence(newIncidence);
            setIsSubmitting(false);
            setIsSuccess(true);
            
            // Clean up form
            setActionDescription('');
            setExpectedResult('');
            setActualResult('');
            setPriority('Media');
            setScreenshot(undefined);
            
            // Auto close after 2.5s
            setTimeout(() => {
                setIsSuccess(false);
                setIsOpen(false);
            }, 2500);
        }, 1000);
    };

    return (
        <>
            {/* Float Animations */}
            <style>
                {`
                    @keyframes pulse-subtle {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }
                    .animate-pulse-subtle {
                        animation: pulse-subtle 4s ease-in-out infinite;
                    }
                `}
            </style>

            {/* Floating Crimson Button */}
            {!isOpen && (
                <div className="hidden md:flex fixed bottom-20 right-4 md:bottom-6 md:right-24 z-[45] flex-col items-end">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="w-12 h-12 md:w-14 md:h-14 bg-rose-600 border-2 border-rose-500/20 hover:border-rose-400 focus:ring-4 focus:ring-rose-300/30 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 group animate-pulse-subtle cursor-pointer"
                        title="Reportar una incidencia / falla técnica"
                    >
                        <span className="material-symbols-outlined text-white text-[24px] md:text-[28px] group-hover:rotate-12 transition-transform">bug_report</span>
                        {/* Notification Dot */}
                        <span className="absolute top-0 right-0 flex h-3.5 w-3.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-rose-500 border border-white"></span>
                        </span>
                    </button>
                </div>
            )}

            {/* Glassmorphic Dialog Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="glass-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col h-auto max-h-[90vh] animate-in slide-in-from-bottom-8 duration-300 glow-blue">
                        
                        {/* Header */}
                        <div className="bg-gradient-to-r from-rose-800 to-rose-700 p-4 md:p-5 flex justify-between items-center text-white shrink-0 shadow-md">
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-[24px]">bug_report</span>
                                <div className="flex flex-col leading-tight">
                                    <span className="font-extrabold text-[15px] uppercase tracking-wide">Reportar Falla Técnica</span>
                                    <span className="text-[10px] text-rose-200 font-medium">Registro directo para soporte de código</span>
                                </div>
                            </div>
                            <button 
                                onClick={() => { if (!isSubmitting) setIsOpen(false); }}
                                className="hover:bg-white/20 p-1.5 rounded-full transition-colors flex items-center justify-center"
                                disabled={isSubmitting}
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Body Form */}
                        {!isSuccess ? (
                            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4 text-left">
                                {/* Auto Context Badges */}
                                <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800/40 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-0.5">Usuario Reportando</label>
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate block" title={currentUser?.email}>
                                            {currentUser?.name || 'Invitado'}
                                        </span>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block mb-0.5">Obra / Proyecto Activo</label>
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate block" title={currentProject?.name}>
                                            {currentProject?.name || 'General / Ninguna'}
                                        </span>
                                    </div>
                                </div>

                                {/* Form Fields */}
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">Módulo o Sección</label>
                                    <select
                                        value={moduleName}
                                        onChange={(e) => setModuleName(e.target.value)}
                                        className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 font-bold transition-all cursor-pointer"
                                    >
                                        <option>Dashboard / Inicio</option>
                                        <option>EDT (Estructura de Trabajo)</option>
                                        <option>Planificación</option>
                                        <option>Control de Calidad</option>
                                        <option>Subcontratos y Pagos</option>
                                        <option>Informes</option>
                                        <option>Otros / General</option>
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">Acción que estabas ejecutando</label>
                                    <input
                                        type="text"
                                        required
                                        value={actionDescription}
                                        onChange={(e) => setActionDescription(e.target.value)}
                                        placeholder="Ej: Intentaba aprobar el Estado de Pago N°2 o arrastrar Gantt"
                                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 transition-all"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">¿Qué resultado esperabas obtener?</label>
                                    <textarea
                                        required
                                        rows={2}
                                        value={expectedResult}
                                        onChange={(e) => setExpectedResult(e.target.value)}
                                        placeholder="Describe la respuesta correcta que debió dar la plataforma..."
                                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 transition-all resize-none"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">¿Qué error o comportamiento incorrecto ocurrió?</label>
                                    <textarea
                                        required
                                        rows={2}
                                        value={actualResult}
                                        onChange={(e) => setActualResult(e.target.value)}
                                        placeholder="Describe la falla, el mensaje de error o la inconsistencia detectada..."
                                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 transition-all resize-none"
                                    />
                                </div>

                                {/* Capture attachment area */}
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">Adjuntar Captura de Pantalla</label>
                                    
                                    {!screenshot ? (
                                        Capacitor.isNativePlatform() ? (
                                            <button
                                                type="button"
                                                onClick={handleMobilePhotoCapture}
                                                className="w-full flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-rose-500/50 dark:hover:border-rose-500/50 rounded-xl p-4 bg-slate-50 dark:bg-slate-800/40 cursor-pointer transition-colors relative group"
                                            >
                                                <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 group-hover:text-rose-500 transition-colors text-[24px]">add_a_photo</span>
                                                <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 mt-1">Tomar Foto o Seleccionar de Galería</span>
                                                <span className="text-[9px] text-slate-400 mt-0.5">Usa la cámara o fotos nativas</span>
                                            </button>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-rose-500/50 dark:hover:border-rose-500/50 rounded-xl p-4 bg-slate-50 dark:bg-slate-800/40 cursor-pointer transition-colors relative group animate-fade-in">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                                <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 group-hover:text-rose-500 transition-colors text-[24px]">add_a_photo</span>
                                                <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 mt-1">Cargar desde Galería o Archivo</span>
                                                <span className="text-[9px] text-slate-400 mt-0.5">O pega directamente una captura con Ctrl+V</span>
                                            </div>
                                        )
                                    ) : (
                                        <div className="relative rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-between p-2">
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <img src={screenshot} alt="Screenshot preview" className="h-12 w-16 object-cover rounded-lg border border-slate-200 dark:border-slate-700 flex-shrink-0" />
                                                <div className="truncate">
                                                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 block">Captura cargada con éxito</span>
                                                    <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-0.5 mt-0.5">
                                                        <span className="material-symbols-outlined text-[10px]">check_circle</span>
                                                        Listo para enviar
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setScreenshot(undefined)}
                                                className="p-1 text-slate-400 hover:text-rose-500 transition-colors rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center mr-1"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest block ml-1">Gravedad / Prioridad</label>
                                        <select
                                            value={priority}
                                            onChange={(e) => setPriority(e.target.value as any)}
                                            className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-rose-500 font-bold transition-all cursor-pointer"
                                        >
                                            <option value="Baja">Baja (Detalles visuales)</option>
                                            <option value="Media">Media (Afecta flujos secundarios)</option>
                                            <option value="Alta">Alta (Bloqueo operacional crítico)</option>
                                        </select>
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full flex items-center justify-center gap-2 py-3 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-800 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-rose-600/25 transition-all active:scale-[0.98] cursor-pointer"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                                    <span>Enviando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-symbols-outlined text-[16px]">send</span>
                                                    <span>Registrar Falla</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            /* Success Screen */
                            <div className="p-8 flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-200 gap-4 shrink-0 min-h-[300px]">
                                <div className="size-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-md">
                                    <span className="material-symbols-outlined text-[48px] font-bold">check_circle</span>
                                </div>
                                <h3 className="text-xl font-extrabold text-slate-900 dark:text-white mt-2">¡Reporte Registrado!</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
                                    La incidencia ha sido enviada con éxito a la base de datos de auditoría. El equipo técnico la revisará directamente a nivel de código.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ReportBugWidget;
